import React, { useCallback, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import { List } from "immutable";
import { formValueSelector } from "redux-form";

import { getCampaignCategories, getEntityId, getSelectCategoryCopy } from "data/entity";

import { getCampaign } from "data/parent-campaign";

import {
  setGatewayError,
  setQuery,
  selectAllocation,
  selectCategory,
  getAllocationType,
  getSearchQuery,
  getSelectedCategory,
  getFilteredAllocations,
  getHasFetched,
  getLoading,
  getSelectedAllocations,
  fetchAllocations,
} from "data/giving-form/allocation-selection";

import { GIVING_FORM_PAGINATION_SCOPE } from "data/giving-form";

import {
  getPaginationInfo,
  getPaginationLoading,
  setInitialPagination,
} from "data/pagination";

import Allocation from "../components/Allocation";
import InputDebounced from "lib/components/InputDebounced";

import AmploButton from "shared/ui/amplo-button";
import { useListenGivingFormRouter } from "shared/custom-hooks/use-listen-giving-form-router";
import { FormattedMessage } from "react-intl";

let timeout;

const SearchAllocations = ({
  location,
  campaignId,
  allocationType,
  entityId,
  fetchAllocations,
  setQuery,
  setInitialPagination,
  selectCategory,
  campaign,
  categories,
  allocations,
  selectedAllocations,
  loading,
  searchQuery,
  paginationLoading,
  pagination,
  paginationInfo,
  selectAllocation,
  totalAmount,
  setGatewayError,
}) => {
  const resultsContainerRef = useRef(null);
  const { autoCategory } = useListenGivingFormRouter({ location });
  const handleSelectCategory = useCallback(
    (category) => {
      selectCategory(category);
      fetchAllocations(campaignId, allocationType, {
        entityId,
        resetPage: true,
        category,
      }).then(({ total }) => setInitialPagination({ total }));
    },
    [campaignId, allocationType, entityId, selectCategory, fetchAllocations, setInitialPagination],
  );

  useEffect(() => {
    if (allocationType !== "campaign") {
      return;
    }
    const category = autoCategory || campaign("giving_form").get("auto_dock_category");
    if (
      category &&
      category !== "" &&
      categories.includes(decodeURIComponent(category))
    ) {
      handleSelectCategory(decodeURIComponent(category));
    }
  }, []);

  const handleInput = useCallback(
    (e) => {
      e.preventDefault();
      const val = e.target.value;

      setQuery(val);

      fetchAllocations(campaignId, allocationType, {
        entityId,
        query: val,
      }).then(({ total }) => setInitialPagination({ total }));
    },
    [campaignId, allocationType, entityId, setQuery, fetchAllocations, setInitialPagination],
  );

  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();

      setQuery(searchQuery);

      fetchAllocations(campaignId, allocationType, {
        entityId,
        query: searchQuery,
      }).then(({ total }) => setInitialPagination({ total }));
    },
    [
      campaignId,
      allocationType,
      entityId,
      searchQuery,
      setQuery,
      fetchAllocations,
      setInitialPagination,
    ],
  );

  const handlePagination = useCallback(() => {
    fetchAllocations(campaignId, allocationType, {
      entityId,
      nextPage: true,
    });
  }, [campaignId, allocationType, entityId, fetchAllocations]);

  const handleAllocationSelect = useCallback(
    (allocation, bool) => {
      if (allocationType === "campaign") {
        const gatewayId =
          selectedAllocations.count() > 0 && selectedAllocations.first().get("gateway_id");

        if (gatewayId && allocation.get("gateway_id") !== gatewayId) {
          setGatewayError(allocation.get("name"));
        } else {
          setGatewayError();
          selectAllocation(allocation, bool, totalAmount);
        }
      } else {
        selectAllocation(allocation, bool, totalAmount);
      }
    },
    [allocationType, selectedAllocations, setGatewayError, selectAllocation, totalAmount],
  );

  const trackScrolling = useCallback(
    (event) => {
      const node = event.target;
      const isBottom = node.scrollHeight - node.scrollTop <= node.clientHeight + 30;

      if (isBottom && !paginationLoading && paginationInfo.hasPagesRemaining) {
        clearTimeout(timeout);
        timeout = setTimeout(() => {
          fetchAllocations(campaignId, allocationType, {
            entityId,
            nextPage: true,
            fundsPage: -1,
          });
        }, 200);
      }
    },
    [
      campaignId,
      allocationType,
      entityId,
      paginationInfo.hasPagesRemaining,
      paginationLoading,
      fetchAllocations,
    ],
  );

  useEffect(() => {
    const node = resultsContainerRef.current;
    if (!node) {
      return undefined;
    }
    node.addEventListener("scroll", trackScrolling);
    return () => {
      clearTimeout(timeout);
      node.removeEventListener("scroll", trackScrolling);
    };
  }, [trackScrolling, allocations, loading, allocationType, pagination.hasPagesRemaining]);

  const renderContent = () => {
    const { hasPagesRemaining } = pagination;
    const content = [];

    if (allocations) {
      allocations.forEach((allocation, idx) => {
        const isInSelected = !!selectedAllocations.find((item) => {
          return allocation.get("id") === item.get("id");
        });

        if (!isInSelected) {
          const key = `${idx}-${allocation.get("name")}`;
          content.push(
            <Allocation
              key={key}
              idx={idx}
              allocation={allocation}
              isSelected={false}
              onSelectAllocation={handleAllocationSelect}
            />,
          );
        }
      });
    }

    if (allocationType !== "campaign" && !(loading || !hasPagesRemaining)) {
      content.push(
        <div key="load-more-button" className="text-center m-sm-b">
          <AmploButton onClick={handlePagination}>load more causes</AmploButton>
        </div>,
      );
    }

    if (loading) {
      content.push(
        <div key="spinner" className="m-md-t m-md-b text-center">
          <i className="fa fa-3x fa-refresh fa-spin" />
        </div>,
      );
    }

    if (content.length > 0) {
      return (
        <div ref={resultsContainerRef} className="search-results">
          {content}
        </div>
      );
    }

    return <div className="text-center p-sm-t p-sm-b">No results found</div>;
  };

  return (
    <div>
      <form
        data-testid="t-giving-form--SearchA-form"
        className="cause-search"
        onSubmit={handleSubmit}>
        <div className="voting-search__input-group">
          <FormattedMessage id="searchFunds">
            {(msg) => (
              <InputDebounced
                className="voting-search__input"
                onChange={handleInput}
                type="search"
                aria-label="Search"
                results="5"
                placeholder={msg}
              />
            )}
          </FormattedMessage>

          <button
            data-testid="t-giving-form--SearchA-submit"
            aria-label="Submit"
            className="voting-search-button"
            type="submit">
            <i className="fa fa-search" />
          </button>
        </div>
      </form>

      {renderContent()}
    </div>
  );
};

SearchAllocations.propTypes = {
  allocations: PropTypes.instanceOf(List),
  selectAllocation: PropTypes.func.isRequired,
  setQuery: PropTypes.func.isRequired,
  setGatewayError: PropTypes.func.isRequired,
  searchQuery: PropTypes.string,
  selectCategoryCopy: PropTypes.string.isRequired,
  selectedAllocations: PropTypes.instanceOf(List).isRequired,
  selectedCategory: PropTypes.string,
  totalAmount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

const selector = formValueSelector("donation");

const mapStateToProps = (state) => {
  return {
    selectCategoryCopy: getSelectCategoryCopy(state),
    loading: getLoading(state),
    fetchedAllocations: getHasFetched(state),
    allocationType: getAllocationType(state),
    searchQuery: getSearchQuery(state),
    allocations: getFilteredAllocations(state),
    selectedAllocations: getSelectedAllocations(state),
    categories: getCampaignCategories(state),
    selectedCategory: getSelectedCategory(state),
    entityId: getEntityId(state),
    campaign: getCampaign(state),
    campaignId: getCampaign(state)("giving_form").get("id"),
    totalAmount: selector(state, "amount"),
    pagination: getPaginationInfo("giving_form")(state),
    paginationLoading: getPaginationLoading("giving_form")(state),
    paginationInfo: getPaginationInfo("giving_form")(state),
  };
};

const mapActionCreators = {
  setGatewayError,
  selectAllocation,
  setQuery,
  selectCategory,
  fetchAllocations,
  setInitialPagination({ total }) {
    return setInitialPagination({ scope: GIVING_FORM_PAGINATION_SCOPE, total });
  },
};

export default connect(mapStateToProps, mapActionCreators)(withRouter(SearchAllocations));
